/**
 * The `pb-vendored://` URI: a reference to a file in a template's reference
 * bundle (`data/reference-bundles/template-websites/<domain>/<relPath>`).
 *
 * IF-4 needs one font reference that three consumers can each resolve their own
 * way — the preview to an R2 URL, the exporter to a bundled `assets/fonts/`
 * path, the saved preview to a data: URI. The alternative was to leave the
 * captured absolute site URL in the CSS and have each consumer map it back
 * through the vendored manifest; that keeps a live-origin URL in the emitted
 * bytes, so any consumer that forgets to map silently HOTLINKS a site that may
 * no longer exist. A scheme with no network meaning cannot fail that way: a
 * consumer that does not resolve it renders no font and is loudly wrong.
 *
 * Pure string handling — no imports, so parsers, the browser preview and the
 * export path can all share it.
 */

export const VENDORED_URI_SCHEME = 'pb-vendored:';
export const VENDORED_URI_PREFIX = 'pb-vendored://';

/** Reference-bundle root for a domain, as an asset-loader logical key. */
export function vendoredBundleRoot(domain) {
  return `data/reference-bundles/template-websites/${domain}`;
}

function normalizeDomain(domain) {
  return String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
}

function normalizeRelPath(relPath) {
  return String(relPath || '')
    .trim()
    .replace(/^\.?\/+/, '');
}

/**
 * @param {string} domain - template site name, e.g. `tcvisualsllc.com`
 * @param {string} relPath - bundle-relative path, e.g. `vendored/abc123.woff2`
 * @returns {string} `pb-vendored://<domain>/<relPath>`, or '' when either half
 *   is missing or carries a character that would make the URI ambiguous.
 */
export function buildVendoredUri(domain, relPath) {
  const host = normalizeDomain(domain);
  const path = normalizeRelPath(relPath);
  if (!host || !path) return '';
  // A domain or path containing URI punctuation would round-trip wrong; refuse
  // rather than emit something parseVendoredUri would read back differently.
  if (!/^[a-z0-9.-]+$/.test(host)) return '';
  if (!/^[A-Za-z0-9._/-]+$/.test(path) || path.includes('..')) return '';
  return `${VENDORED_URI_PREFIX}${host}/${path}`;
}

export function isVendoredUri(value) {
  return String(value || '').startsWith(VENDORED_URI_PREFIX);
}

/**
 * @param {string} uri
 * @returns {{domain: string, relPath: string, logicalKey: string}|null}
 */
export function parseVendoredUri(uri) {
  const raw = String(uri || '').trim();
  if (!isVendoredUri(raw)) return null;
  const rest = raw.slice(VENDORED_URI_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0) return null;
  const domain = rest.slice(0, slash);
  const relPath = rest.slice(slash + 1);
  if (!buildVendoredUri(domain, relPath)) return null;
  return { domain, relPath, logicalKey: `${vendoredBundleRoot(domain)}/${relPath}` };
}

const VENDORED_URI_RE = /pb-vendored:\/\/[A-Za-z0-9._/-]+/g;

/** Every distinct `pb-vendored://` URI appearing in a CSS (or HTML) string. */
export function collectVendoredUris(text) {
  const source = String(text || '');
  if (!source.includes(VENDORED_URI_PREFIX)) return [];
  const out = new Set();
  for (const match of source.match(VENDORED_URI_RE) || []) {
    if (parseVendoredUri(match)) out.add(match);
  }
  return Array.from(out);
}

/** Filename for a vendored URI — the content-hashed name from the bundle. */
export function vendoredFileName(uri) {
  const parsed = parseVendoredUri(uri);
  if (!parsed) return '';
  return parsed.relPath.split('/').pop() || '';
}
